import { useState, useEffect, useCallback, useMemo } from 'react';
import { Typography, Segmented, Skeleton } from 'antd';
import { useTranslation } from 'react-i18next';
import {
  RiseOutlined,
  FallOutlined,
  CalendarOutlined,
  CheckCircleOutlined,
  PercentageOutlined,
  DollarOutlined,
  TrophyOutlined,
  UserOutlined,
  ClockCircleOutlined,
  LockOutlined,
  ReloadOutlined,
  CrownOutlined,
} from '@ant-design/icons';
import { telegram } from '../../utils/telegram';
import { providerMeApi } from '../../api/providerMeApi';
import { usePullToRefresh } from '../../hooks/usePullToRefresh';
import PullToRefreshIndicator from '../shared/PullToRefreshIndicator';
import type {
  AnalyticsData,
  AnalyticsPeriod as PeriodKey,
} from '../../types/provider';

const { Text } = Typography;

const PERIOD_KEYS: PeriodKey[] = ['week', 'month', 'quarter', 'year'];

// ─── Status colors (match TodayTab + BookingsTab) ────────────────────────────

const STATUS_COLORS = {
  PENDING: '#faad14',
  CONFIRMED: '#1677ff',
  COMPLETED: '#1f8b4c',
  CANCELLED: '#ff4d4f',
};

// ─── Helpers ────────────────────────────────────────────────────────────────

function formatCompactNumber(value: number, currencyLabel: string): string {
  if (value >= 1_000_000) {
    const millions = value / 1_000_000;
    return `${millions.toFixed(millions < 10 ? 2 : 1).replace(/\.?0+$/, '')} mln ${currencyLabel}`;
  }
  if (value >= 1_000) {
    const thousands = value / 1_000;
    return `${thousands.toFixed(0)}k ${currencyLabel}`;
  }
  return `${value.toLocaleString('uz-UZ')} ${currencyLabel}`;
}

function formatFullNumber(value: number): string {
  return value.toLocaleString('uz-UZ');
}

function computeTrend(current: number, previous: number): {
  direction: 'up' | 'down' | 'flat';
  percent: number;
} {
  if (previous === 0) {
    return { direction: current > 0 ? 'up' : 'flat', percent: 0 };
  }
  const diff = ((current - previous) / previous) * 100;
  if (Math.abs(diff) < 0.5) return { direction: 'flat', percent: 0 };
  return { direction: diff > 0 ? 'up' : 'down', percent: Math.abs(diff) };
}

// ─── PeriodSwitcher ─────────────────────────────────────────────────────────

function PeriodSwitcher({
  value,
  onChange,
}: {
  value: PeriodKey;
  onChange: (next: PeriodKey) => void;
}) {
  const { t } = useTranslation('provider');
  return (
    <Segmented
      block
      value={value}
      onChange={(v) => {
        telegram.hapticFeedback.selection();
        onChange(v as PeriodKey);
      }}
      options={PERIOD_KEYS.map((key) => ({
        value: key,
        label: t(`analytics.period.${key}`),
      }))}
    />
  );
}

// ─── HeroRevenueCard ────────────────────────────────────────────────────────

function HeroRevenueCard({ data }: { data: AnalyticsData }) {
  const { t } = useTranslation('provider');
  const trend = computeTrend(data.totalRevenue, data.prevRevenue);
  const currencyLabel = t('common.currency');
  const trendChipBg =
    trend.direction === 'up'
      ? 'rgba(255,255,255,0.20)'
      : trend.direction === 'down'
        ? 'rgba(255,77,79,0.22)'
        : 'rgba(255,255,255,0.16)';
  const trendIcon =
    trend.direction === 'up' ? <RiseOutlined /> : trend.direction === 'down' ? <FallOutlined /> : null;

  // Sparkline geometry
  const points = data.revenueByPoint;
  const max = Math.max(...points.map((p) => p.value), 1);
  const spark = points
    .map((p, i) => {
      const x = (i / Math.max(points.length - 1, 1)) * 100;
      const y = 100 - (p.value / max) * 100;
      return `${x},${y}`;
    })
    .join(' ');

  return (
    <div
      style={{
        position: 'relative',
        borderRadius: 18,
        background:
          'linear-gradient(135deg, #1f8b4c 0%, #27ae60 55%, #2dba71 100%)',
        padding: '16px 16px 18px',
        marginBottom: 12,
        overflow: 'hidden',
        boxShadow: '0 6px 20px rgba(31,139,76,0.25)',
      }}
    >
      {/* Decorative blur orb */}
      <div
        aria-hidden
        style={{
          position: 'absolute',
          top: -40,
          right: -30,
          width: 140,
          height: 140,
          borderRadius: '50%',
          background: 'rgba(255,255,255,0.10)',
          filter: 'blur(6px)',
        }}
      />

      <div style={{ position: 'relative', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <Text
            style={{
              fontSize: 11,
              color: 'rgba(255,255,255,0.75)',
              textTransform: 'uppercase',
              letterSpacing: 0.6,
              fontWeight: 600,
              display: 'block',
              marginBottom: 4,
            }}
          >
            {t('analytics.hero.revenueLabel')}
          </Text>
          <Text
            style={{
              fontSize: 26,
              fontWeight: 800,
              color: '#fff',
              letterSpacing: -0.6,
              display: 'block',
              lineHeight: 1.15,
            }}
          >
            {formatFullNumber(data.totalRevenue)}
            <span style={{ fontSize: 14, fontWeight: 500, marginLeft: 6, opacity: 0.8 }}>
              {currencyLabel}
            </span>
          </Text>

          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 10 }}>
            {trend.direction !== 'flat' && (
              <div
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 4,
                  fontSize: 12,
                  fontWeight: 700,
                  color: '#fff',
                  background: trendChipBg,
                  borderRadius: 20,
                  padding: '3px 10px',
                  backdropFilter: 'blur(6px)',
                }}
              >
                {trendIcon}
                <span>{trend.percent.toFixed(1)}%</span>
              </div>
            )}
            <Text style={{ fontSize: 11, color: 'rgba(255,255,255,0.7)' }}>
              {trend.direction === 'flat'
                ? t('analytics.hero.trendFlat')
                : t('analytics.hero.comparedTo')}
            </Text>
          </div>
        </div>

        {/* Inline sparkline */}
        <svg
          viewBox="0 0 100 100"
          preserveAspectRatio="none"
          style={{
            width: 84,
            height: 40,
            marginLeft: 12,
            flexShrink: 0,
            opacity: 0.95,
          }}
        >
          <defs>
            <linearGradient id="sparkFill" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="#fff" stopOpacity="0.45" />
              <stop offset="100%" stopColor="#fff" stopOpacity="0" />
            </linearGradient>
          </defs>
          <polyline
            points={spark}
            fill="none"
            stroke="#fff"
            strokeWidth="2.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          <polygon
            points={`0,100 ${spark} 100,100`}
            fill="url(#sparkFill)"
          />
        </svg>
      </div>
    </div>
  );
}

// ─── KpiGrid ────────────────────────────────────────────────────────────────

function KpiGrid({ data }: { data: AnalyticsData }) {
  const { t } = useTranslation('provider');
  const currencyLabel = t('common.currency');

  const cards = [
    {
      label: t('analytics.kpi.totalBookings'),
      value: formatFullNumber(data.totalBookings),
      icon: <CalendarOutlined />,
      color: '#1677ff',
      bg: 'var(--bl-info-soft, #e7f1fe)',
    },
    {
      label: t('analytics.kpi.completed'),
      value: formatFullNumber(data.completedBookings),
      icon: <CheckCircleOutlined />,
      color: '#1f8b4c',
      bg: 'var(--bl-success-soft, #e8f7ee)',
    },
    {
      label: t('analytics.kpi.completionRate'),
      value: `${data.completionRate.toFixed(1)}%`,
      icon: <PercentageOutlined />,
      color: '#d46b08',
      bg: '#fff8ed',
    },
    {
      label: t('analytics.kpi.avgRevenue'),
      value: formatCompactNumber(data.avgRevenue, currencyLabel),
      icon: <DollarOutlined />,
      color: 'var(--bl-text-strong, #0f172a)',
      bg: 'var(--bl-surface-soft, #f7f8fa)',
    },
  ];

  return (
    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginBottom: 12 }}>
      {cards.map((c) => (
        <div
          key={c.label}
          style={{
            borderRadius: 14,
            background: c.bg,
            padding: '12px 14px',
            display: 'flex',
            flexDirection: 'column',
            gap: 8,
            minHeight: 92,
          }}
        >
          <div
            style={{
              width: 32,
              height: 32,
              borderRadius: 10,
              background: c.color + '20',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: 15,
              color: c.color,
            }}
          >
            {c.icon}
          </div>
          <div>
            <div
              style={{
                fontSize: 18,
                fontWeight: 800,
                lineHeight: 1.15,
                color: 'var(--bl-text-strong, #0f172a)',
                letterSpacing: -0.3,
              }}
            >
              {c.value}
            </div>
            <Text
              style={{
                fontSize: 11,
                color: 'var(--bl-text-muted, #9ca3af)',
                fontWeight: 500,
                display: 'block',
                marginTop: 2,
              }}
            >
              {c.label}
            </Text>
          </div>
        </div>
      ))}
    </div>
  );
}

// ─── SectionHeader ──────────────────────────────────────────────────────────

function SectionHeader({ title, subtitle }: { title: string; subtitle?: string }) {
  return (
    <div style={{ marginBottom: 10, marginTop: 4 }}>
      <Text
        style={{
          fontSize: 14,
          fontWeight: 700,
          color: 'var(--bl-text-strong, #0f172a)',
          display: 'block',
        }}
      >
        {title}
      </Text>
      {subtitle && (
        <Text
          style={{
            fontSize: 11,
            color: 'var(--bl-text-muted, #9ca3af)',
            display: 'block',
            marginTop: 2,
          }}
        >
          {subtitle}
        </Text>
      )}
    </div>
  );
}

// ─── RevenueTrendChart ──────────────────────────────────────────────────────

function RevenueTrendChart({ data }: { data: AnalyticsData }) {
  const { t } = useTranslation('provider');
  const currencyLabel = t('common.currency');
  const points = data.revenueByPoint;
  const max = Math.max(...points.map((p) => p.value), 1);

  const CHART_HEIGHT = 132;
  const BAR_GAP = 8;
  const labelHeight = 22;

  return (
    <div
      style={{
        borderRadius: 16,
        background: 'var(--bl-surface, #fff)',
        boxShadow: 'var(--bl-shadow-soft, 0 1px 6px rgba(0,0,0,0.07))',
        padding: '14px 14px 12px',
        marginBottom: 12,
      }}
    >
      <SectionHeader
        title={t('analytics.trend.title')}
        subtitle={t('analytics.trend.subtitle')}
      />

      <div
        style={{
          display: 'flex',
          alignItems: 'flex-end',
          gap: BAR_GAP,
          height: CHART_HEIGHT,
          marginTop: 8,
        }}
      >
        {points.map((p, i) => {
          const isMax = p.value === max;
          const heightPct = max > 0 ? (p.value / max) * 100 : 0;
          return (
            <div
              key={i}
              style={{
                flex: 1,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'flex-end',
                height: '100%',
                position: 'relative',
              }}
            >
              {/* Value label on top */}
              <Text
                style={{
                  fontSize: 10,
                  fontWeight: 600,
                  color: isMax ? '#1f8b4c' : 'var(--bl-text-muted, #9ca3af)',
                  marginBottom: 4,
                  whiteSpace: 'nowrap',
                }}
              >
                {p.value >= 1_000_000
                  ? `${(p.value / 1_000_000).toFixed(1)}M`
                  : p.value >= 1_000
                    ? `${(p.value / 1_000).toFixed(0)}k`
                    : p.value.toString()}
              </Text>

              {/* Bar */}
              <div
                style={{
                  width: '100%',
                  maxWidth: 28,
                  height: `${heightPct}%`,
                  minHeight: 4,
                  borderRadius: '6px 6px 2px 2px',
                  background: isMax
                    ? 'linear-gradient(180deg, #2dba71 0%, #1f8b4c 100%)'
                    : 'linear-gradient(180deg, #aed8ff 0%, #66aaf0 100%)',
                  transition: 'height 0.35s ease',
                }}
              />
            </div>
          );
        })}
      </div>

      {/* X-axis labels */}
      <div
        style={{
          display: 'flex',
          gap: BAR_GAP,
          marginTop: 6,
          height: labelHeight - 6,
        }}
      >
        {points.map((p, i) => (
          <div
            key={i}
            style={{
              flex: 1,
              textAlign: 'center',
              fontSize: 11,
              color: 'var(--bl-text-muted, #6b7280)',
              fontWeight: 500,
            }}
          >
            {p.label}
          </div>
        ))}
      </div>

      {/* Footer note */}
      <Text
        style={{
          display: 'block',
          fontSize: 10,
          color: 'var(--bl-text-muted, #9ca3af)',
          marginTop: 4,
          textAlign: 'right',
        }}
      >
        {currencyLabel}
      </Text>
    </div>
  );
}

// ─── StatusBreakdown ────────────────────────────────────────────────────────

function StatusBreakdown({ data }: { data: AnalyticsData }) {
  const { t } = useTranslation('provider');

  const segments = [
    { key: 'COMPLETED', value: data.completedBookings, color: STATUS_COLORS.COMPLETED },
    { key: 'CONFIRMED', value: data.confirmedBookings, color: STATUS_COLORS.CONFIRMED },
    { key: 'PENDING', value: data.pendingBookings, color: STATUS_COLORS.PENDING },
    { key: 'CANCELLED', value: data.cancelledBookings, color: STATUS_COLORS.CANCELLED },
  ];

  const total = segments.reduce((sum, s) => sum + s.value, 0);
  const visible = segments.filter((s) => s.value > 0);

  return (
    <div
      style={{
        borderRadius: 16,
        background: 'var(--bl-surface, #fff)',
        boxShadow: 'var(--bl-shadow-soft, 0 1px 6px rgba(0,0,0,0.07))',
        padding: '14px 14px 14px',
        marginBottom: 12,
      }}
    >
      <SectionHeader title={t('analytics.status.title')} />

      {/* Stacked bar */}
      <div
        style={{
          display: 'flex',
          width: '100%',
          height: 10,
          borderRadius: 6,
          overflow: 'hidden',
          background: 'var(--bl-surface-soft, #f0f2f5)',
          marginTop: 4,
          marginBottom: 12,
        }}
      >
        {visible.map((seg) => {
          const widthPct = total > 0 ? (seg.value / total) * 100 : 0;
          return (
            <div
              key={seg.key}
              style={{
                width: `${widthPct}%`,
                background: seg.color,
                transition: 'width 0.3s ease',
              }}
              title={`${seg.key}: ${seg.value}`}
            />
          );
        })}
      </div>

      {/* Legend */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
        {segments.map((seg) => {
          const pct = total > 0 ? (seg.value / total) * 100 : 0;
          return (
            <div
              key={seg.key}
              style={{ display: 'flex', alignItems: 'center', gap: 8 }}
            >
              <div
                style={{
                  width: 9,
                  height: 9,
                  borderRadius: '50%',
                  background: seg.color,
                  flexShrink: 0,
                }}
              />
              <div style={{ display: 'flex', flexDirection: 'column', minWidth: 0 }}>
                <Text
                  style={{
                    fontSize: 11,
                    color: 'var(--bl-text-muted, #6b7280)',
                    lineHeight: 1.2,
                  }}
                >
                  {t(`analytics.status.labels.${seg.key}`)}
                </Text>
                <Text
                  style={{
                    fontSize: 13,
                    fontWeight: 700,
                    color: 'var(--bl-text-strong, #0f172a)',
                    lineHeight: 1.2,
                  }}
                >
                  {seg.value}
                  <span
                    style={{
                      fontSize: 10,
                      fontWeight: 500,
                      color: 'var(--bl-text-muted, #9ca3af)',
                      marginLeft: 4,
                    }}
                  >
                    {pct.toFixed(0)}%
                  </span>
                </Text>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ─── TopServicesList ────────────────────────────────────────────────────────

function TopServicesList({ data }: { data: AnalyticsData }) {
  const { t } = useTranslation('provider');
  const currencyLabel = t('common.currency');
  const items = data.topServices;

  if (items.length === 0) {
    return (
      <div
        style={{
          borderRadius: 16,
          background: 'var(--bl-surface, #fff)',
          boxShadow: 'var(--bl-shadow-soft, 0 1px 6px rgba(0,0,0,0.07))',
          padding: '14px 14px 16px',
          marginBottom: 12,
        }}
      >
        <SectionHeader
          title={t('analytics.topServices.title')}
          subtitle={t('analytics.topServices.subtitle')}
        />
        <Text style={{ fontSize: 12, color: 'var(--bl-text-muted, #9ca3af)' }}>
          {t('analytics.topServices.empty')}
        </Text>
      </div>
    );
  }

  const max = Math.max(...items.map((s) => s.revenue), 1);

  return (
    <div
      style={{
        borderRadius: 16,
        background: 'var(--bl-surface, #fff)',
        boxShadow: 'var(--bl-shadow-soft, 0 1px 6px rgba(0,0,0,0.07))',
        padding: '14px 14px 14px',
        marginBottom: 12,
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
        <SectionHeader
          title={t('analytics.topServices.title')}
          subtitle={t('analytics.topServices.subtitle')}
        />
        <TrophyOutlined style={{ fontSize: 16, color: '#fa8c16' }} />
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        {items.map((s, idx) => {
          const widthPct = (s.revenue / max) * 100;
          return (
            <div key={s.id}>
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'baseline',
                  marginBottom: 5,
                  gap: 8,
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 0, flex: 1 }}>
                  <div
                    style={{
                      width: 20,
                      height: 20,
                      borderRadius: 6,
                      background:
                        idx === 0
                          ? 'linear-gradient(135deg, #fa8c16, #faad14)'
                          : 'var(--bl-surface-soft, #f0f2f5)',
                      color: idx === 0 ? '#fff' : 'var(--bl-text-muted, #6b7280)',
                      fontSize: 11,
                      fontWeight: 700,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flexShrink: 0,
                    }}
                  >
                    {idx + 1}
                  </div>
                  <Text
                    style={{
                      fontSize: 13,
                      fontWeight: 600,
                      color: 'var(--bl-text-strong, #0f172a)',
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                    }}
                  >
                    {s.name}
                  </Text>
                </div>
                <Text
                  style={{
                    fontSize: 13,
                    fontWeight: 700,
                    color: 'var(--bl-text-strong, #0f172a)',
                    flexShrink: 0,
                    whiteSpace: 'nowrap',
                  }}
                >
                  {formatCompactNumber(s.revenue, currencyLabel)}
                </Text>
              </div>

              {/* Progress bar */}
              <div
                style={{
                  height: 6,
                  borderRadius: 4,
                  background: 'var(--bl-surface-soft, #f0f2f5)',
                  overflow: 'hidden',
                }}
              >
                <div
                  style={{
                    width: `${widthPct}%`,
                    height: '100%',
                    background:
                      idx === 0
                        ? 'linear-gradient(90deg, #fa8c16, #faad14)'
                        : 'linear-gradient(90deg, #66aaf0, #3390ec)',
                    transition: 'width 0.4s ease',
                  }}
                />
              </div>

              <Text
                style={{
                  fontSize: 10,
                  color: 'var(--bl-text-muted, #9ca3af)',
                  marginTop: 3,
                  display: 'block',
                }}
              >
                {t('analytics.topServices.bookingsCount', { count: s.bookings })}
              </Text>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ─── TopCustomersList ───────────────────────────────────────────────────────

function getInitials(name: string): string {
  return name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((p) => p[0]?.toUpperCase() ?? '')
    .join('');
}

function TopCustomersList({ data }: { data: AnalyticsData }) {
  const { t } = useTranslation('provider');
  const currencyLabel = t('common.currency');
  const items = data.topCustomers;

  if (items.length === 0) {
    return (
      <div
        style={{
          borderRadius: 16,
          background: 'var(--bl-surface, #fff)',
          boxShadow: 'var(--bl-shadow-soft, 0 1px 6px rgba(0,0,0,0.07))',
          padding: '14px 14px 16px',
          marginBottom: 12,
        }}
      >
        <SectionHeader
          title={t('analytics.topCustomers.title')}
          subtitle={t('analytics.topCustomers.subtitle')}
        />
        <Text style={{ fontSize: 12, color: 'var(--bl-text-muted, #9ca3af)' }}>
          {t('analytics.topCustomers.empty')}
        </Text>
      </div>
    );
  }

  const avatarPalette = ['#3390ec', '#1f8b4c', '#fa8c16', '#9254de', '#eb2f96'];

  return (
    <div
      style={{
        borderRadius: 16,
        background: 'var(--bl-surface, #fff)',
        boxShadow: 'var(--bl-shadow-soft, 0 1px 6px rgba(0,0,0,0.07))',
        padding: '14px 14px 8px',
        marginBottom: 12,
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
        <SectionHeader
          title={t('analytics.topCustomers.title')}
          subtitle={t('analytics.topCustomers.subtitle')}
        />
        <UserOutlined style={{ fontSize: 16, color: 'var(--bl-accent, #3390ec)' }} />
      </div>

      <div style={{ display: 'flex', flexDirection: 'column' }}>
        {items.map((c, idx) => {
          const color = avatarPalette[idx % avatarPalette.length];
          const isLast = idx === items.length - 1;
          return (
            <div
              key={c.id}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 10,
                padding: '8px 0',
                borderBottom: isLast ? 'none' : '1px solid var(--bl-divider, #f0f0f0)',
              }}
            >
              <div
                style={{
                  width: 34,
                  height: 34,
                  borderRadius: '50%',
                  background: color + '18',
                  color,
                  fontSize: 12,
                  fontWeight: 700,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0,
                }}
              >
                {getInitials(c.name)}
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <Text
                  style={{
                    fontSize: 13,
                    fontWeight: 600,
                    color: 'var(--bl-text-strong, #0f172a)',
                    display: 'block',
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                  }}
                >
                  {c.name}
                </Text>
                <Text
                  style={{
                    fontSize: 11,
                    color: 'var(--bl-text-muted, #9ca3af)',
                    display: 'block',
                  }}
                >
                  {t('analytics.topCustomers.bookings', { count: c.bookings })}
                </Text>
              </div>
              <div style={{ textAlign: 'right', flexShrink: 0 }}>
                <Text
                  style={{
                    fontSize: 13,
                    fontWeight: 700,
                    color: 'var(--bl-text-strong, #0f172a)',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {formatCompactNumber(c.totalSpent, currencyLabel)}
                </Text>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ─── PeakHoursChart ─────────────────────────────────────────────────────────

function PeakHoursChart({ data }: { data: AnalyticsData }) {
  const { t } = useTranslation('provider');
  const items = data.peakHours;

  if (items.length === 0) {
    return (
      <div
        style={{
          borderRadius: 16,
          background: 'var(--bl-surface, #fff)',
          boxShadow: 'var(--bl-shadow-soft, 0 1px 6px rgba(0,0,0,0.07))',
          padding: '14px 14px 16px',
          marginBottom: 12,
        }}
      >
        <SectionHeader
          title={t('analytics.peakHours.title')}
          subtitle={t('analytics.peakHours.subtitle')}
        />
        <Text style={{ fontSize: 12, color: 'var(--bl-text-muted, #9ca3af)' }}>
          {t('analytics.peakHours.empty')}
        </Text>
      </div>
    );
  }

  const max = Math.max(...items.map((h) => h.bookings), 1);

  return (
    <div
      style={{
        borderRadius: 16,
        background: 'var(--bl-surface, #fff)',
        boxShadow: 'var(--bl-shadow-soft, 0 1px 6px rgba(0,0,0,0.07))',
        padding: '14px 14px 14px',
        marginBottom: 12,
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
        <SectionHeader
          title={t('analytics.peakHours.title')}
          subtitle={t('analytics.peakHours.subtitle')}
        />
        <ClockCircleOutlined style={{ fontSize: 16, color: '#9254de' }} />
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        {items.map((h, idx) => {
          const widthPct = (h.bookings / max) * 100;
          const intensity = idx === 0 ? '#9254de' : idx === 1 ? '#b37feb' : '#d3adf7';
          return (
            <div key={h.hour} style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <Text
                style={{
                  fontSize: 12,
                  fontWeight: 600,
                  color: 'var(--bl-text-strong, #0f172a)',
                  width: 44,
                  flexShrink: 0,
                  fontVariantNumeric: 'tabular-nums',
                }}
              >
                {h.hour}
              </Text>
              <div
                style={{
                  flex: 1,
                  height: 22,
                  borderRadius: 6,
                  background: 'var(--bl-surface-soft, #f7f8fa)',
                  overflow: 'hidden',
                  position: 'relative',
                }}
              >
                <div
                  style={{
                    width: `${widthPct}%`,
                    height: '100%',
                    background: `linear-gradient(90deg, ${intensity}, ${intensity}cc)`,
                    borderRadius: 6,
                    transition: 'width 0.4s ease',
                  }}
                />
              </div>
              <Text
                style={{
                  fontSize: 12,
                  fontWeight: 700,
                  color: 'var(--bl-text-strong, #0f172a)',
                  width: 36,
                  textAlign: 'right',
                  flexShrink: 0,
                  fontVariantNumeric: 'tabular-nums',
                }}
              >
                {h.bookings}
              </Text>
            </div>
          );
        })}
      </div>

      <Text
        style={{
          display: 'block',
          fontSize: 10,
          color: 'var(--bl-text-muted, #9ca3af)',
          marginTop: 8,
          textAlign: 'right',
        }}
      >
        {t('analytics.peakHours.bookings', { count: max })}
      </Text>
    </div>
  );
}

// ─── Empty state ────────────────────────────────────────────────────────────

function AnalyticsEmpty() {
  const { t } = useTranslation('provider');
  return (
    <div
      style={{
        textAlign: 'center',
        padding: '32px 20px',
        background: 'var(--bl-surface, #fff)',
        borderRadius: 16,
        border: '1px solid var(--bl-divider, #ebebeb)',
        marginTop: 16,
      }}
    >
      <div style={{ fontSize: 36, marginBottom: 10 }}>📊</div>
      <Text style={{ fontSize: 15, fontWeight: 600, display: 'block', marginBottom: 4 }}>
        {t('analytics.empty.title')}
      </Text>
      <Text type="secondary" style={{ fontSize: 13, display: 'block' }}>
        {t('analytics.empty.description')}
      </Text>
    </div>
  );
}

// ─── Premium upgrade prompt ─────────────────────────────────────────────────

function PremiumUpgradePrompt() {
  const { t } = useTranslation('provider');
  return (
    <div
      style={{
        position: 'relative',
        textAlign: 'center',
        padding: '36px 24px 28px',
        background:
          'linear-gradient(135deg, rgba(250,140,22,0.08) 0%, rgba(146,84,222,0.10) 100%)',
        border: '1px solid rgba(250,140,22,0.22)',
        borderRadius: 18,
        marginTop: 16,
        overflow: 'hidden',
      }}
    >
      {/* Decorative blur orbs */}
      <div
        aria-hidden
        style={{
          position: 'absolute',
          top: -40,
          left: -20,
          width: 120,
          height: 120,
          borderRadius: '50%',
          background: 'rgba(250,140,22,0.18)',
          filter: 'blur(8px)',
          pointerEvents: 'none',
        }}
      />
      <div
        aria-hidden
        style={{
          position: 'absolute',
          bottom: -40,
          right: -20,
          width: 120,
          height: 120,
          borderRadius: '50%',
          background: 'rgba(146,84,222,0.16)',
          filter: 'blur(8px)',
          pointerEvents: 'none',
        }}
      />

      <div style={{ position: 'relative' }}>
        {/* Lock disc */}
        <div
          style={{
            width: 72,
            height: 72,
            borderRadius: '50%',
            background:
              'linear-gradient(135deg, #fa8c16 0%, #d46b08 60%, #722ed1 100%)',
            color: '#fff',
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: 30,
            margin: '0 auto 14px',
            boxShadow:
              '0 10px 28px rgba(250,140,22,0.30), inset 0 1px 0 rgba(255,255,255,0.22)',
          }}
        >
          <LockOutlined />
        </div>

        {/* PREMIUM badge */}
        <div
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 5,
            background: 'linear-gradient(135deg, #fa8c16 0%, #d46b08 100%)',
            color: '#fff',
            fontSize: 10,
            fontWeight: 800,
            letterSpacing: 0.6,
            padding: '3px 10px',
            borderRadius: 999,
            marginBottom: 10,
            boxShadow: '0 4px 12px rgba(250,140,22,0.32)',
          }}
        >
          <CrownOutlined style={{ fontSize: 10 }} />
          {t('analytics.premium.badge')}
        </div>

        <Text
          style={{
            fontSize: 17,
            fontWeight: 800,
            color: 'var(--bl-text-strong, #0f172a)',
            display: 'block',
            marginBottom: 6,
            letterSpacing: -0.2,
          }}
        >
          {t('analytics.premium.title')}
        </Text>
        <Text
          style={{
            fontSize: 13,
            color: 'var(--bl-text-muted, #6b7280)',
            display: 'block',
            maxWidth: 300,
            margin: '0 auto 18px',
            lineHeight: 1.5,
          }}
        >
          {t('analytics.premium.description')}
        </Text>

        {/* Upgrade CTA (disabled until owner-side flow ready) */}
        <button
          type="button"
          disabled
          style={{
            minHeight: 46,
            padding: '0 22px',
            border: 'none',
            borderRadius: 12,
            background: 'linear-gradient(135deg, #fa8c16 0%, #d46b08 100%)',
            color: '#fff',
            fontSize: 14,
            fontWeight: 700,
            cursor: 'not-allowed',
            opacity: 0.85,
            boxShadow:
              '0 6px 18px rgba(250,140,22,0.30), inset 0 1px 0 rgba(255,255,255,0.18)',
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 8,
            WebkitTapHighlightColor: 'transparent',
            outline: 'none',
            letterSpacing: 0.15,
            fontFamily: 'inherit',
          }}
        >
          <CrownOutlined />
          <span>{t('analytics.premium.upgradeBtn')}</span>
        </button>
      </div>
    </div>
  );
}

// ─── Error state with retry ─────────────────────────────────────────────────

function AnalyticsErrorState({ onRetry }: { onRetry: () => void }) {
  const { t } = useTranslation('provider');
  const [pressed, setPressed] = useState(false);

  return (
    <div
      style={{
        textAlign: 'center',
        padding: '48px 32px 32px',
        marginTop: 16,
      }}
    >
      <div
        style={{
          width: 72,
          height: 72,
          borderRadius: '50%',
          background:
            'linear-gradient(135deg, rgba(250,173,20,0.14) 0%, rgba(250,173,20,0.28) 100%)',
          border: '1px solid rgba(250,173,20,0.20)',
          color: '#faad14',
          fontSize: 30,
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          margin: '0 auto 16px',
          boxShadow: '0 8px 24px rgba(250,173,20,0.10)',
        }}
      >
        <ReloadOutlined />
      </div>
      <Text
        style={{
          fontSize: 15,
          fontWeight: 700,
          color: 'var(--bl-text-strong, #0f172a)',
          display: 'block',
          marginBottom: 6,
        }}
      >
        {t('analytics.errorState.title')}
      </Text>
      <Text
        style={{
          fontSize: 13,
          color: 'var(--bl-text-muted, #6b7280)',
          display: 'block',
          maxWidth: 280,
          margin: '0 auto 16px',
          lineHeight: 1.45,
        }}
      >
        {t('analytics.errorState.description')}
      </Text>

      <button
        type="button"
        onClick={onRetry}
        onTouchStart={() => setPressed(true)}
        onTouchEnd={() => setPressed(false)}
        onTouchCancel={() => setPressed(false)}
        onMouseDown={() => setPressed(true)}
        onMouseUp={() => setPressed(false)}
        onMouseLeave={() => setPressed(false)}
        style={{
          minHeight: 44,
          padding: '0 20px',
          border: '1px solid rgba(22,119,255,0.18)',
          borderRadius: 12,
          background: 'linear-gradient(135deg, #3390ec 0%, #1677ff 100%)',
          color: '#fff',
          fontSize: 14,
          fontWeight: 700,
          cursor: 'pointer',
          boxShadow:
            '0 4px 14px rgba(22,119,255,0.28), inset 0 1px 0 rgba(255,255,255,0.18)',
          transform: pressed ? 'scale(0.985)' : 'scale(1)',
          transition: 'transform 0.1s ease',
          WebkitTapHighlightColor: 'transparent',
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 8,
          outline: 'none',
          letterSpacing: 0.15,
          fontFamily: 'inherit',
        }}
      >
        <ReloadOutlined />
        <span>{t('analytics.errorState.retryBtn')}</span>
      </button>
    </div>
  );
}

// ─── Skeleton ───────────────────────────────────────────────────────────────

function AnalyticsTabSkeleton() {
  return (
    <div className="bl-animate-in">
      {/* Hero */}
      <div
        style={{
          borderRadius: 18,
          background: 'var(--bl-surface-soft, #f0f2f5)',
          padding: '16px 16px 18px',
          marginBottom: 12,
        }}
      >
        <Skeleton.Input active size="small" style={{ width: 120, height: 11, marginBottom: 8 }} />
        <Skeleton.Input active size="small" style={{ width: 200, height: 26, marginBottom: 10 }} />
        <Skeleton.Input active size="small" style={{ width: 160, height: 14 }} />
      </div>

      {/* KPI grid */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginBottom: 12 }}>
        {[0, 1, 2, 3].map((i) => (
          <div
            key={i}
            style={{
              borderRadius: 14,
              background: 'var(--bl-surface-soft, #f7f8fa)',
              padding: '12px 14px',
              minHeight: 92,
              display: 'flex',
              flexDirection: 'column',
              gap: 8,
            }}
          >
            <Skeleton.Avatar active size={32} shape="square" />
            <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
              <Skeleton.Input active size="small" style={{ width: 60, height: 16 }} />
              <Skeleton.Input active size="small" style={{ width: 80, height: 10 }} />
            </div>
          </div>
        ))}
      </div>

      {/* Card placeholders */}
      {[0, 1, 2, 3, 4].map((i) => (
        <div
          key={i}
          style={{
            borderRadius: 16,
            background: 'var(--bl-surface, #fff)',
            boxShadow: 'var(--bl-shadow-soft, 0 1px 6px rgba(0,0,0,0.07))',
            padding: '14px 14px',
            marginBottom: 12,
            minHeight: 110,
          }}
        >
          <Skeleton active paragraph={{ rows: 2 }} title={{ width: 140 }} />
        </div>
      ))}
    </div>
  );
}

// ─── Main Component ─────────────────────────────────────────────────────────

type AnalyticsError = 'premium' | 'generic';

function classifyError(err: unknown): AnalyticsError {
  const e = err as {
    response?: { status?: number; data?: { code?: string } };
  };
  const code = e?.response?.data?.code;
  if (code === 'FEATURE_REQUIRES_PREMIUM_PLAN') return 'premium';
  return 'generic';
}

export default function AnalyticsTab() {
  const { i18n } = useTranslation('provider');
  const [period, setPeriod] = useState<PeriodKey>('month');
  const [data, setData] = useState<AnalyticsData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<AnalyticsError | null>(null);

  const loadData = useCallback(async (selected: PeriodKey) => {
    setLoading(true);
    setError(null);
    try {
      const res = await providerMeApi.getAnalytics(selected);
      setData(res);
    } catch (err) {
      setError(classifyError(err));
      setData(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void loadData(period);
  }, [loadData, period]);

  // Backend labels are localized via Accept-Language → refetch when language changes
  useEffect(() => {
    const handler = () => {
      void loadData(period);
    };
    i18n.on('languageChanged', handler);
    return () => {
      i18n.off('languageChanged', handler);
    };
  }, [i18n, loadData, period]);

  const hasAnyData = useMemo(() => {
    if (!data) return false;
    return data.totalBookings > 0 || data.totalRevenue > 0;
  }, [data]);

  const { containerRef, pullDistance, isRefreshing, progress } =
    usePullToRefresh<HTMLDivElement>({
      onRefresh: () => loadData(period),
      disabled: loading,
    });

  return (
    <div
      ref={containerRef}
      style={{ height: '100%', overflowY: 'auto', position: 'relative' }}
    >
      <PullToRefreshIndicator
        pullDistance={pullDistance}
        isRefreshing={isRefreshing}
        progress={progress}
      />
      <div style={{ padding: 16, paddingBottom: 32 }}>
        {/* Period switcher — premium gated bo'lsa ham foydalanuvchi davrni o'zgartira oladi */}
        {error !== 'premium' && (
          <div className="bl-animate-in bl-animate-in-delay-1" style={{ marginBottom: 12 }}>
            <PeriodSwitcher value={period} onChange={setPeriod} />
          </div>
        )}

        {loading ? (
          <AnalyticsTabSkeleton />
        ) : error === 'premium' ? (
          <PremiumUpgradePrompt />
        ) : error === 'generic' ? (
          <AnalyticsErrorState onRetry={() => void loadData(period)} />
        ) : !data || !hasAnyData ? (
          <AnalyticsEmpty />
        ) : (
          <div key="ready">
            <div className="bl-animate-in bl-animate-in-delay-2">
              <HeroRevenueCard data={data} />
            </div>
            <div className="bl-animate-in bl-animate-in-delay-2">
              <KpiGrid data={data} />
            </div>
            <div className="bl-animate-in bl-animate-in-delay-3">
              <RevenueTrendChart data={data} />
            </div>
            <div className="bl-animate-in bl-animate-in-delay-3">
              <StatusBreakdown data={data} />
            </div>
            <div className="bl-animate-in bl-animate-in-delay-3">
              <TopServicesList data={data} />
            </div>
            <div className="bl-animate-in bl-animate-in-delay-3">
              <TopCustomersList data={data} />
            </div>
            <div className="bl-animate-in bl-animate-in-delay-3">
              <PeakHoursChart data={data} />
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
